import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CompetitionServiceInstance from "../../services/CompetitionService";
import { setCoverageCompetitions } from "../../store/slices/UtilitySlice";
import { truncateString } from "../../helpers/StringFormatHelper";


const ui = {
    section: {
        padding: "100px 20px",
        background: "#f3f4f6",
        color: "#0f172a",
        position: "relative",
        overflow: "hidden",
    },
    container: { maxWidth: 1160, margin: "0 auto" },
    subtitle: {
        display: "inline-block",
        fontSize: 12,
        letterSpacing: ".5px",
        textTransform: "uppercase",
        color: "#2563eb",
        border: "1px solid rgba(37,99,235,.25)",
        padding: "6px 12px",
        borderRadius: 999,
        background: "rgba(37,99,235,.08)",
    },
    title: { margin: "10px 0 0", fontSize: 28, fontWeight: 800 },
    grid: { display: "grid", gap: 16, gridTemplateColumns: "repeat(2, minmax(0,1fr))" },
    gridMd: { display: "grid", gap: 16, gridTemplateColumns: "repeat(3, 1fr)" },
    gridLg: { display: "grid", gap: 16, gridTemplateColumns: "repeat(6, 1fr)" },

    tile: {
        position: "relative",
        borderRadius: 16,
        border: "1px solid rgba(0,0,0,.08)",
        background: "linear-gradient(180deg, rgba(255,255,255,.98), rgba(255,255,255,.94))",
        padding: 14,
        height: 108,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 8px 22px rgba(0,0,0,.06)",
        transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
    },
    tileHover: {
        transform: "translateY(-4px)",
        boxShadow: "0 14px 32px rgba(0,0,0,.12)",
    },
    logo: {
        width: 44,
        height: 44,
        objectFit: "contain",
        imageRendering: "auto",
    },
    name: { marginTop: 10, fontSize: 12, color: "#1f2937", textAlign: "center", fontWeight: 700 },
    country: { marginTop: 2, fontSize: 11, color: "#6b7280" },
};

const wellKnownLeagueIds = [39, 140, 78, 61, 135, 2, 3, 94, 88, 203, 179, 143];

export default function SupportedCompetitions({
    leagueIds = wellKnownLeagueIds,
    title = "Supported Competitions & Coverage",
    subtitle = "Top leagues and tournaments covered worldwide",
}) {
    const leagues = useSelector((state) => state.Utility.coverageCompetitions);
    const [hover, setHover] = React.useState(-1);
    const [grid, setGrid] = React.useState(ui.grid);
    const dispatch = useDispatch();

	useEffect(() => {
		const fetchCoveredLeagues = async () => {
			try {
				const response = await CompetitionServiceInstance.getCoveredLeagues();
				const responseData = response.data;
				const { success, result } = responseData;
				if (success === true) {
					dispatch(setCoverageCompetitions(result.data));
				}
			} catch (error) {

			}
		};

		fetchCoveredLeagues();
	}, []);

    React.useEffect(() => {
        const onResize = () => {
            const w = window.innerWidth;
            if (w >= 1200) setGrid(ui.gridLg);
            else if (w >= 768) setGrid(ui.gridMd);
            else setGrid(ui.grid);
        };
        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    return (
        <section style={ui.section}>
            <div style={ui.container}>
                <div style={{ textAlign: "center", marginBottom: 18 }}>
                    <h2 style={ui.title}>{title}</h2>
                    <p style={{ margin: 0, color: "#6b7280" }}>{subtitle}</p>
                </div>

                <div style={grid}>
                    {(leagues || Array.from({ length: leagueIds.length })).map((l, i) =>
                        !leagues ? (
                            <div key={i} style={ui.tile}>
                                <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(0,0,0,.06)" }} />
                                <div style={{ width: "70%", height: 10, marginTop: 12, background: "rgba(0,0,0,.06)", borderRadius: 6 }} />
                            </div>
                        ) : (
                            <article
                                key={l.id}
                                style={{ ...ui.tile, ...(hover === i ? ui.tileHover : null) }}
                                onMouseEnter={() => setHover(i)}
                                onMouseLeave={() => setHover(-1)}
                                title={l.country ? `${l.name} — ${l.country.name}` : l.name}
                            >
                                {l.logo ? (
                                    <img src={l.logo} alt={l.name} style={ui.logo} />
                                ) : (
                                    <div style={{ ...ui.logo, background: "rgba(0,0,0,.06)", borderRadius: 12 }} />
                                )}
                                <div style={ui.name}>{truncateString(l.name, 20)}</div>
                                {l.country.name ? <div style={ui.country}>{l.country.name}</div> : null}
                            </article>
                        )
                    )}
                </div>
            </div>

            <div
                aria-hidden
                style={{
                    position: "absolute",
                    inset: "auto -20% -40% auto",
                    width: 280,
                    height: 280,
                    filter: "blur(24px)",
                    background: "radial-gradient(closest-side, rgba(37,99,235,.20), transparent 70%)",
                    pointerEvents: "none",
                }}
            />
            <div
                aria-hidden
                style={{
                    position: "absolute",
                    inset: "auto auto -35% -20%",
                    width: 220,
                    height: 220,
                    filter: "blur(20px)",
                    background: "radial-gradient(closest-side, rgba(16,185,129,.18), transparent 70%)",
                    pointerEvents: "none",
                }}
            />
        </section>
    );
}
